<script setup lang="ts">
import { computed, onUnmounted, ref, watch } from 'vue';
import { useDicomMetaStore } from '@/src/store/dicom-web/dicom-meta-store';
import { useDicomWebStore } from '@/src/store/dicom-web/dicom-web-store';
import { useViewStore } from '@/src/store/views';
import { isMobileBrowser } from '@/src/utils/mobileVolume';
import OriginalPanoramaButton from './OriginalPanoramaButton.vue';

const props = defineProps<{ volumeKey: string }>();

const dicomMeta = useDicomMetaStore();
const dicomWeb = useDicomWebStore();
const viewStore = useViewStore();
const mobileBrowser = isMobileBrowser();

const currentIndex = ref(0);
const imageUrl = ref<string | null>(null);
const imageLoading = ref(false);
const advancedLoading = ref(false);
const errorMessage = ref('');

const instances = computed(() =>
  (dicomMeta.volumeInstances[props.volumeKey] ?? [])
    .map((instanceKey) => dicomMeta.instanceInfo[instanceKey])
    .filter(Boolean)
    .sort(
      ({ InstanceNumber: a }, { InstanceNumber: b }) => Number(a) - Number(b)
    )
);

const sliceCount = computed(() => instances.value.length);
const currentInstanceNumber = computed(
  () =>
    instances.value[currentIndex.value]?.InstanceNumber ??
    currentIndex.value + 1
);

function goToSlice(index: number) {
  const lastIndex = Math.max(0, sliceCount.value - 1);
  currentIndex.value = Math.min(lastIndex, Math.max(0, index));
}

function onWheel(event: WheelEvent) {
  if (!event.deltaY) return;
  goToSlice(currentIndex.value + (event.deltaY > 0 ? 1 : -1));
}

function onKeydown(event: KeyboardEvent) {
  if (event.key === 'ArrowDown' || event.key === 'ArrowRight') {
    event.preventDefault();
    goToSlice(currentIndex.value + 1);
  }
  if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') {
    event.preventDefault();
    goToSlice(currentIndex.value - 1);
  }
}

async function loadAdvancedViewer(layoutName = 'Axial Coronal Sagittal') {
  if (advancedLoading.value) return;
  advancedLoading.value = true;
  preloadGeneration++;
  imageUrl.value = null;
  dicomWeb.clearVolumePreviews();
  try {
    await dicomWeb.downloadVolume(props.volumeKey);
    if (dicomWeb.volumes[props.volumeKey]?.state === 'Done') {
      viewStore.switchToNamedLayout(layoutName);
    } else {
      // Restore the lightweight viewer if the full-volume import failed after
      // its rendered-image cache was released.
      try {
        imageUrl.value = await dicomWeb.fetchVolumeSlice(
          props.volumeKey,
          currentIndex.value
        );
        startBackgroundPreload(
          props.volumeKey,
          currentIndex.value,
          sliceCount.value
        );
      } catch {
        errorMessage.value = 'No se pudo restaurar el visor 2D.';
      }
    }
  } finally {
    advancedLoading.value = false;
  }
}

let sliceRequest = 0;
let preloadGeneration = 0;

function startBackgroundPreload(
  volumeKey: string,
  center: number,
  count: number
) {
  const generation = ++preloadGeneration;

  // Keep only nearby cuts warm; preloading hundreds of rendered images can
  // exhaust the memory of the same mobile browser this view is meant to help.
  const pendingIndexes: number[] = [];
  for (let offset = 1; offset <= 2; offset++) {
    const before = center - offset;
    const after = center + offset;
    if (before >= 0) pendingIndexes.push(before);
    if (after < count) pendingIndexes.push(after);
  }

  let cursor = 0;
  const worker = async () => {
    while (generation === preloadGeneration && cursor < pendingIndexes.length) {
      const sliceIndex = pendingIndexes[cursor++];
      try {
        await dicomWeb.fetchVolumeSlice(volumeKey, sliceIndex);
      } catch {
        // A failed background request can be retried if the user navigates to it.
      }
    }
  };

  Promise.all(
    Array.from({ length: Math.min(2, pendingIndexes.length) }, () => worker())
  ).catch(() => undefined);
}

onUnmounted(() => {
  preloadGeneration++;
});

watch(
  [() => props.volumeKey, currentIndex, sliceCount],
  async ([, index, count], previous) => {
    if (!count) return;

    if (!previous || previous[0] !== props.volumeKey) {
      const middleIndex = Math.floor(count / 2);
      if (currentIndex.value !== middleIndex) {
        currentIndex.value = middleIndex;
        return;
      }
    }

    const request = ++sliceRequest;
    const instance = instances.value[index];
    const cachedImage = instance
      ? dicomWeb.sliceImages[instance.SopInstanceUID]
      : null;

    if (cachedImage) {
      imageUrl.value = cachedImage;
      imageLoading.value = false;
      errorMessage.value = '';
      startBackgroundPreload(props.volumeKey, index, count);
      return;
    }

    imageLoading.value = true;
    errorMessage.value = '';
    try {
      const nextImage = await dicomWeb.fetchVolumeSlice(props.volumeKey, index);
      if (request !== sliceRequest) return;
      imageUrl.value = nextImage;
      startBackgroundPreload(props.volumeKey, index, count);
    } catch (error) {
      if (request !== sliceRequest) return;
      errorMessage.value =
        error instanceof Error
          ? error.message
          : 'No se pudo cargar este corte.';
    } finally {
      if (request === sliceRequest) imageLoading.value = false;
    }
  },
  { immediate: true }
);
</script>

<template>
  <section class="quick-stack" aria-label="Visor DICOM 2D rápido">
    <header class="quick-stack__header">
      <div>
        <div class="quick-stack__mode">
          <v-icon size="18">mdi-layers-outline</v-icon>
          Modo 2D rápido
        </div>
        <p v-if="mobileBrowser">
          MPR y 3D cargarán todos los cortes con resolución adaptada al móvil.
          Revise el detalle original en este visor 2D.
        </p>
        <p v-else>
          Se precargan los cortes cercanos. El volumen 3D no se descarga.
        </p>
      </div>
      <div class="quick-stack__actions">
        <OriginalPanoramaButton :study-uid="dicomMeta.volumeStudy[volumeKey]" />
        <v-btn
          v-if="
            viewStore.namedLayouts['Panorámica'] &&
            !viewStore.disabledViewTypes.includes('Panoramic')
          "
          color="light-blue-accent-2"
          variant="flat"
          size="small"
          prepend-icon="mdi-tooth-outline"
          :loading="advancedLoading"
          @click="loadAdvancedViewer('Panorámica')"
        >
          Abrir panorámica
        </v-btn>
        <v-btn
          v-if="
            viewStore.namedLayouts['Corte transversal'] &&
            !viewStore.disabledViewTypes.includes('Oblique')
          "
          color="light-blue-accent-2"
          variant="flat"
          size="small"
          prepend-icon="mdi-axis-arrow"
          :loading="advancedLoading"
          @click="loadAdvancedViewer('Corte transversal')"
        >
          Abrir cuadro transversal
        </v-btn>
        <v-btn
          color="light-blue-accent-2"
          variant="outlined"
          size="small"
          prepend-icon="mdi-view-grid-outline"
          :disabled="advancedLoading"
          @click="loadAdvancedViewer()"
        >
          Cargar visor MPR
        </v-btn>
      </div>
    </header>

    <div
      class="quick-stack__viewport"
      tabindex="0"
      @wheel.prevent="onWheel"
      @keydown="onKeydown"
    >
      <img
        v-if="imageUrl"
        :src="imageUrl"
        :alt="`Corte DICOM ${currentIndex + 1} de ${sliceCount}`"
      />

      <div v-if="imageLoading" class="quick-stack__loading" role="status">
        <v-progress-circular indeterminate color="light-blue-accent-2" />
        <span>Cargando corte {{ currentIndex + 1 }}…</span>
      </div>

      <div v-else-if="errorMessage" class="quick-stack__error" role="alert">
        <v-icon>mdi-alert-circle-outline</v-icon>
        <span>{{ errorMessage }}</span>
      </div>

      <div class="quick-stack__counter">
        Imagen {{ currentIndex + 1 }} / {{ sliceCount }} · Instancia
        {{ currentInstanceNumber }}
      </div>

      <button
        class="quick-stack__step quick-stack__step--previous"
        type="button"
        :disabled="currentIndex === 0"
        aria-label="Corte anterior"
        @click="goToSlice(currentIndex - 1)"
      >
        <v-icon>mdi-chevron-left</v-icon>
      </button>
      <button
        class="quick-stack__step quick-stack__step--next"
        type="button"
        :disabled="currentIndex >= sliceCount - 1"
        aria-label="Corte siguiente"
        @click="goToSlice(currentIndex + 1)"
      >
        <v-icon>mdi-chevron-right</v-icon>
      </button>
    </div>

    <footer class="quick-stack__footer">
      <v-icon size="18">mdi-mouse-move-down</v-icon>
      <span
        >Use la rueda del mouse, las flechas o el control para navegar.</span
      >
      <v-slider
        v-model="currentIndex"
        class="quick-stack__slider"
        color="light-blue-accent-2"
        hide-details
        :min="0"
        :max="Math.max(0, sliceCount - 1)"
        :step="1"
        aria-label="Seleccionar corte DICOM"
      />
    </footer>
  </section>
</template>

<style scoped>
.quick-stack {
  min-width: 0;
  min-height: 0;
  flex: 1;
  display: flex;
  flex-direction: column;
  background: #07131b;
  color: white;
}

.quick-stack__header,
.quick-stack__footer {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 12px 18px;
  background: #0d202c;
  border-color: rgb(255 255 255 / 10%);
}

.quick-stack__header {
  justify-content: space-between;
  border-bottom: 1px solid rgb(255 255 255 / 10%);
}

.quick-stack__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.quick-stack__header p {
  margin: 3px 0 0;
  color: rgb(255 255 255 / 62%);
  font-size: 0.75rem;
}

.quick-stack__mode {
  display: flex;
  align-items: center;
  gap: 7px;
  font-size: 0.875rem;
  font-weight: 600;
}

.quick-stack__viewport {
  position: relative;
  min-height: 0;
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  outline: none;
  background: black;
}

.quick-stack__viewport:focus-visible {
  box-shadow: inset 0 0 0 2px rgb(64 196 255 / 65%);
}

.quick-stack__viewport img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  user-select: none;
}

.quick-stack__loading,
.quick-stack__error {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  background: rgb(0 0 0 / 62%);
  color: rgb(255 255 255 / 78%);
  font-size: 0.8rem;
}

.quick-stack__counter {
  position: absolute;
  top: 12px;
  left: 12px;
  padding: 5px 8px;
  border-radius: 4px;
  background: rgb(0 0 0 / 68%);
  color: rgb(255 255 255 / 76%);
  font: 0.72rem/1.2 monospace;
}

.quick-stack__step {
  position: absolute;
  top: 50%;
  width: 40px;
  height: 52px;
  display: grid;
  place-items: center;
  border: 1px solid rgb(255 255 255 / 16%);
  border-radius: 6px;
  transform: translateY(-50%);
  background: rgb(6 25 36 / 78%);
  color: white;
  cursor: pointer;
}

.quick-stack__step:disabled {
  opacity: 0.28;
  cursor: default;
}

.quick-stack__step--previous {
  left: 12px;
}

.quick-stack__step--next {
  right: 12px;
}

.quick-stack__footer {
  min-height: 54px;
  border-top: 1px solid rgb(255 255 255 / 10%);
  color: rgb(255 255 255 / 62%);
  font-size: 0.75rem;
}

.quick-stack__slider {
  min-width: 140px;
  margin-left: auto;
}

@media (max-width: 720px) {
  .quick-stack__header {
    align-items: stretch;
    flex-direction: column;
  }

  .quick-stack__footer span {
    display: none;
  }

  .quick-stack__slider {
    width: 100%;
  }
}
</style>
